import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../components/DashboardLayout';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, Calendar, LayoutGrid,
  Truck, Package, CheckCircle, Clock, AlertCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// ─── Konstanten ───────────────────────────────────────────────────────────────
const DAYS_DE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const MONTHS_DE = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

// ─── Status-Farben ────────────────────────────────────────────────────────────
const STATUS_STYLES = {
  imported:    { dot: 'bg-blue-500',   chip: 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300',   icon: Clock },
  in_progress: { dot: 'bg-amber-500',  chip: 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-300', icon: Package },
  completed:   { dot: 'bg-emerald-500',chip: 'bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300', icon: CheckCircle },
};

const CAT_DOT = {
  tabak:            'bg-amber-400',
  shop:             'bg-sky-400',
  frische_backshop: 'bg-emerald-400',
};

// ─── Status-Label ─────────────────────────────────────────────────────────────
const STATUS_LABEL = { imported: 'Importiert', in_progress: 'In Bearbeitung', completed: 'Abgeschlossen' };

// ─── Delivery-Chip (compact) ──────────────────────────────────────────────────
function DeliveryChip({ delivery, onClick }) {
  const style = STATUS_STYLES[delivery.status] || STATUS_STYLES.imported;
  return (
    <button
      data-testid={`delivery-chip-${delivery.id}`}
      onClick={onClick}
      className={`w-full text-left px-2 py-1 rounded-md border text-xs font-medium transition-all hover:shadow-md hover:scale-[1.02] ${style.chip} truncate flex items-center gap-1`}
    >
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${style.dot}`} />
      <span className="truncate">{delivery.supplier_name}</span>
    </button>
  );
}

// ─── Delivery-Karte (Wochenansicht) ──────────────────────────────────────────
function DeliveryCard({ delivery, onClick }) {
  const style = STATUS_STYLES[delivery.status] || STATUS_STYLES.imported;
  const StatusIcon = style.icon;
  const catDot = CAT_DOT[delivery.product_category] || 'bg-gray-400';
  return (
    <button
      data-testid={`delivery-card-${delivery.id}`}
      onClick={onClick}
      className={`w-full text-left p-3 rounded-xl border-l-4 shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5 ${style.chip} border-l-${delivery.status === 'completed' ? 'emerald' : delivery.status === 'in_progress' ? 'amber' : 'blue'}-500`}
      style={{ borderLeftColor: delivery.status === 'completed' ? '#10b981' : delivery.status === 'in_progress' ? '#f59e0b' : '#3b82f6' }}
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <span className="font-semibold text-sm leading-tight truncate">{delivery.supplier_name}</span>
        <StatusIcon className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
      </div>
      <div className="text-xs opacity-75 font-mono">WBS {delivery.wbs_number}</div>
      <div className="flex items-center gap-1.5 mt-1.5">
        <span className={`w-2 h-2 rounded-full ${catDot}`} />
        <span className="text-xs opacity-70">
          {delivery.total_items > 0 ? `${delivery.checked_items}/${delivery.total_items} Pos.` : STATUS_LABEL[delivery.status] || delivery.status}
        </span>
      </div>
    </button>
  );
}

// ─── ISO-Woche berechnen ──────────────────────────────────────────────────────
function getISOWeek(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const week1 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
}

// Montag der ISO-Woche
function getMondayOfISOWeek(year, week) {
  const simple = new Date(year, 0, 1 + (week - 1) * 7);
  const dow = simple.getDay();
  const ISOweekStart = simple;
  if (dow <= 4) ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
  else ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
  return ISOweekStart;
}

// ─── Hauptkomponente ──────────────────────────────────────────────────────────
export default function LieferKalender() {
  const navigate = useNavigate();

  // Heute
  const today = new Date();
  const [view, setView] = useState('month'); // 'month' | 'week'
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1-12
  const [week, setWeek] = useState(getISOWeek(today));
  const [byDate, setByDate] = useState({});
  const [loading, setLoading] = useState(false);

  // Daten laden
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = view === 'month'
        ? { year, month }
        : { year, week };
      const { data } = await axios.get(`${API}/api/lieferung/calendar`, {
        headers: authHeader(),
        params,
      });
      setByDate(data.by_date || {});
    } catch (err) {
      toast.error('Kalender konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [view, year, month, week]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Navigation ──────────────────────────────────────────────────────────────
  function goBack() {
    if (view === 'month') {
      if (month === 1) { setYear(y => y - 1); setMonth(12); }
      else setMonth(m => m - 1);
    } else {
      // Woche zurück
      const mon = getMondayOfISOWeek(year, week);
      mon.setDate(mon.getDate() - 7);
      setYear(mon.getFullYear());
      setWeek(getISOWeek(mon));
    }
  }

  function goForward() {
    if (view === 'month') {
      if (month === 12) { setYear(y => y + 1); setMonth(1); }
      else setMonth(m => m + 1);
    } else {
      const mon = getMondayOfISOWeek(year, week);
      mon.setDate(mon.getDate() + 7);
      setYear(mon.getFullYear());
      setWeek(getISOWeek(mon));
    }
  }

  function goToToday() {
    const t = new Date();
    setYear(t.getFullYear());
    setMonth(t.getMonth() + 1);
    setWeek(getISOWeek(t));
  }

  // ── Header-Titel ────────────────────────────────────────────────────────────
  function headerTitle() {
    if (view === 'month') return `${MONTHS_DE[month - 1]} ${year}`;
    const mon = getMondayOfISOWeek(year, week);
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);
    const fmt = d => `${d.getDate()}.${d.getMonth() + 1}.`;
    return `KW ${week} · ${fmt(mon)}–${fmt(sun)}${sun.getFullYear()}`;
  }

  function isToday(dateStr) {
    const t = today;
    const d = new Date(dateStr);
    return d.getFullYear() === t.getFullYear() && d.getMonth() === t.getMonth() && d.getDate() === t.getDate();
  }

  // ── Monatsansicht: Tage aufbauen ────────────────────────────────────────────
  function buildMonthGrid() {
    const firstDay = new Date(year, month - 1, 1);
    // In Deutschland: Montag = 1. Tag der Woche
    let startDow = (firstDay.getDay() + 6) % 7; // 0=Mo,6=So
    const daysInMonth = new Date(year, month, 0).getDate();
    const cells = [];
    // Leere Vorzellen
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const dateObj = new Date(year, month - 1, d);
      const isoKey = dateObj.toISOString().slice(0, 10);
      cells.push({ day: d, iso: isoKey, deliveries: byDate[isoKey] || [] });
    }
    return cells;
  }

  // ── Wochenansicht: 7 Tage ab Montag ────────────────────────────────────────
  function buildWeekDays() {
    const mon = getMondayOfISOWeek(year, week);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(mon);
      d.setDate(mon.getDate() + i);
      const isoKey = d.toISOString().slice(0, 10);
      return { date: d, iso: isoKey, deliveries: byDate[isoKey] || [] };
    });
  }

  const totalDeliveries = Object.values(byDate).reduce((acc, arr) => acc + arr.length, 0);

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 max-w-7xl mx-auto">

        {/* ── Header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center shadow-md">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">Liefer-Kalender</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {loading ? 'Lädt…' : `${totalDeliveries} Lieferung${totalDeliveries !== 1 ? 'en' : ''}`}
              </p>
            </div>
          </div>

          {/* View Toggle */}
          <div className="flex items-center gap-2">
            <div className="flex rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-900">
              <button
                data-testid="view-toggle-month"
                onClick={() => setView('month')}
                className={`px-4 py-2 text-sm font-medium flex items-center gap-1.5 transition-colors ${view === 'month' ? 'bg-blue-600 text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
              >
                <LayoutGrid className="w-4 h-4" />
                Monat
              </button>
              <button
                data-testid="view-toggle-week"
                onClick={() => setView('week')}
                className={`px-4 py-2 text-sm font-medium flex items-center gap-1.5 transition-colors ${view === 'week' ? 'bg-blue-600 text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
              >
                <Calendar className="w-4 h-4" />
                Woche
              </button>
            </div>
          </div>
        </div>

        {/* ── Navigationsleiste ── */}
        <div className="flex items-center justify-between mb-4">
          <button
            data-testid="nav-prev"
            onClick={goBack}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-600 dark:text-gray-400"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white" data-testid="calendar-title">
              {headerTitle()}
            </h2>
            <button
              data-testid="nav-today"
              onClick={goToToday}
              className="px-3 py-1 text-xs font-medium rounded-lg border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 dark:border-blue-700 dark:text-blue-300 dark:bg-blue-900/20 dark:hover:bg-blue-900/40 transition-colors"
            >
              Heute
            </button>
          </div>

          <button
            data-testid="nav-next"
            onClick={goForward}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-600 dark:text-gray-400"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* ── Legende ── */}
        <div className="flex flex-wrap items-center gap-4 mb-4 text-xs text-gray-500 dark:text-gray-400">
          {Object.entries(STATUS_STYLES).map(([key, val]) => (
            <div key={key} className="flex items-center gap-1.5">
              <span className={`w-2.5 h-2.5 rounded-full ${val.dot}`} />
              {STATUS_LABEL[key]}
            </div>
          ))}
        </div>

        {/* ── Loading ── */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* ── Monatsansicht ── */}
        {!loading && view === 'month' && <MonthView cells={buildMonthGrid()} isToday={isToday} onDeliveryClick={id => navigate(`/lieferung/${id}`)} />}

        {/* ── Wochenansicht ── */}
        {!loading && view === 'week' && <WeekView days={buildWeekDays()} isToday={isToday} onDeliveryClick={id => navigate(`/lieferung/${id}`)} />}

        {/* ── Leer-Zustand ── */}
        {!loading && totalDeliveries === 0 && (
          <div className="text-center py-16 text-gray-400 dark:text-gray-600">
            <Truck className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-base font-medium">Keine Lieferungen in diesem Zeitraum</p>
            <p className="text-sm mt-1">Lieferungen werden automatisch nach dem PDF-Import angezeigt.</p>
          </div>
        )}

      </div>
    </DashboardLayout>
  );
}

// ─── Monatsansicht-Komponente ─────────────────────────────────────────────────
function MonthView({ cells, isToday, onDeliveryClick }) {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
      {/* Wochentag-Header */}
      <div className="grid grid-cols-7 border-b border-gray-200 dark:border-gray-700">
        {DAYS_DE.map(d => (
          <div key={d} className="py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            {d}
          </div>
        ))}
      </div>

      {/* Kalender-Grid */}
      <div className="grid grid-cols-7">
        {cells.map((cell, idx) => {
          if (!cell) {
            return <div key={`empty-${idx}`} className="min-h-[90px] bg-gray-50/50 dark:bg-gray-950/50 border-b border-r border-gray-100 dark:border-gray-800" />;
          }
          const todayStyle = isToday(cell.iso);
          return (
            <div
              key={cell.iso}
              data-testid={`month-cell-${cell.iso}`}
              className={`min-h-[90px] p-1.5 border-b border-r border-gray-100 dark:border-gray-800 
                ${todayStyle ? 'bg-blue-50 dark:bg-blue-950/30' : 'bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-850'}`}
            >
              {/* Tageszahl */}
              <div className={`w-7 h-7 flex items-center justify-center rounded-full text-sm font-medium mb-1 mx-auto
                ${todayStyle ? 'bg-blue-600 text-white' : 'text-gray-700 dark:text-gray-300'}`}>
                {cell.day}
              </div>
              {/* Lieferungen */}
              <div className="space-y-0.5">
                {cell.deliveries.slice(0, 3).map(d => (
                  <DeliveryChip key={d.id} delivery={d} onClick={() => onDeliveryClick(d.id)} />
                ))}
                {cell.deliveries.length > 3 && (
                  <div className="text-xs text-gray-400 dark:text-gray-500 pl-1">
                    +{cell.deliveries.length - 3} weitere
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Wochenansicht-Komponente ─────────────────────────────────────────────────
function WeekView({ days, isToday, onDeliveryClick }) {
  const maxDeliveries = Math.max(...days.map(d => d.deliveries.length), 1);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
      {/* Wochentag-Header */}
      <div className="grid grid-cols-7 border-b border-gray-200 dark:border-gray-700">
        {days.map(({ date, iso }) => {
          const todayStyle = isToday(iso);
          return (
            <div
              key={iso}
              data-testid={`week-header-${iso}`}
              className={`p-3 text-center border-r border-gray-100 dark:border-gray-800 last:border-r-0
                ${todayStyle ? 'bg-blue-50 dark:bg-blue-950/30' : ''}`}
            >
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {DAYS_DE[(date.getDay() + 6) % 7]}
              </div>
              <div className={`text-2xl font-bold mt-0.5 leading-none
                ${todayStyle ? 'text-blue-600 dark:text-blue-400' : 'text-gray-800 dark:text-gray-200'}`}>
                {date.getDate()}
              </div>
              <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                {MONTHS_DE[date.getMonth()].slice(0, 3)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Lieferungen je Tag */}
      <div className={`grid grid-cols-7 divide-x divide-gray-100 dark:divide-gray-800`}
           style={{ minHeight: `${Math.max(maxDeliveries * 100, 200)}px` }}>
        {days.map(({ date, iso, deliveries }) => {
          const todayStyle = isToday(iso);
          return (
            <div
              key={iso}
              data-testid={`week-day-${iso}`}
              className={`p-2 space-y-2 
                ${todayStyle ? 'bg-blue-50/50 dark:bg-blue-950/20' : 'bg-white dark:bg-gray-900'}`}
            >
              {deliveries.length === 0 && (
                <div className="h-full flex items-start justify-center pt-6 text-gray-300 dark:text-gray-700">
                  <Truck className="w-5 h-5" />
                </div>
              )}
              {deliveries.map(d => (
                <DeliveryCard key={d.id} delivery={d} onClick={() => onDeliveryClick(d.id)} />
              ))}
            </div>
          );
        })}
      </div>

      {/* Wochenstatistik */}
      <div className="border-t border-gray-100 dark:border-gray-800 px-4 py-2 flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-950/50">
        <span className="font-medium text-gray-700 dark:text-gray-300">
          {days.reduce((acc, d) => acc + d.deliveries.length, 0)} Lieferungen diese Woche
        </span>
        {Object.entries(
          days.flatMap(d => d.deliveries).reduce((acc, d) => {
            acc[d.status] = (acc[d.status] || 0) + 1;
            return acc;
          }, {})
        ).map(([s, count]) => (
          <div key={s} className="flex items-center gap-1">
            <span className={`w-2 h-2 rounded-full ${STATUS_STYLES[s]?.dot || 'bg-gray-400'}`} />
            {count} {STATUS_LABEL[s] || s}
          </div>
        ))}
      </div>
    </div>
  );
}
